import { useMutation, useQuery, useSubscription } from "@apollo/client";
import { Form, Formik } from "formik";
import gql from "graphql-tag";
import * as Cookies from "js-cookie";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import * as Yup from "yup";
import { toastSuccessOptions } from "../../../App";
import getCurrentUserInformation from "../../../GraphQL/Query/getCurrentUserInformation";
import { formatPasswordChangeDate } from "../../../utils/formatPasswordChangedWhen";
import { handleGraphQLErrors } from "../../../utils/handleGraphQLError";
import { ApplicationDisplay } from "../../SharedComponents/ApplicationDisplay/ApplicationDisplay";
import { Button } from "../../SharedComponents/Button/Button";
import { DeleteButton } from "../../SharedComponents/DeleteButton/DeleteButton";
import { DeleteModalContent } from "../../SharedComponents/DeleteModalContent/DeleteModalContent";
import {
  EditableInformation,
  EditableInformationItemWrapper,
  EditableInformationTextInput,
  EditableInformationTextSelect,
} from "../../SharedComponents/EditableInformation/EditableInformation";
import { EditHeader } from "../../SharedComponents/EditHeader/EditHeader";
import { Modal } from "../../SharedComponents/Modal/Modal";
import { AdminUsersControlList } from "../AdminUsersControlList/AdminUsersControlList";
import "./AdminAccountsEdit.css";

const queryCountries = (countries, query) => {
  return countries.filter((country) =>
    country.name.toLowerCase().includes(query.toLowerCase())
  );
};

export const AdminAccountsEdit = () => {
  const { t } = useTranslation();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const id = Cookies.get("primary_account");
  const accessLevel = Cookies.get("access_level");
  /** @type {object} params */
  const params = useParams();
  const history = useHistory();
  const { data, error, loading } = useQuery(getAccountQuery, {
    variables: {
      id: params.id,
    },
  });

  const {
    data: usersData,
    error: usersError,
    loading: usersLoading,
  } = useQuery(getUsers, {
    variables: { accountIds: [params.id] },
    fetchPolicy: "network-only",
  });

  const { data: appsData, error: appsError, loading: appsLoading } = useQuery(
    getAppsQuery,
    {
      variables: { accountIds: [params.id] },
      fetchPolicy: "network-only",
    }
  );

  const { data: countryData } = useQuery(countriesQuery);

  useSubscription(onAppAccountAddedSubscription, {
    variables: { accountId: params.id },
    onSubscriptionData: ({ client: { cache }, subscriptionData }) => {
      const prev = cache.readQuery({
        query: getAppsQuery,
        variables: { accountIds: [params.id] },
      });

      cache.writeQuery({
        query: getAppsQuery,
        variables: { accountIds: [params.id] },
        data: {
          ...prev,
          apps: [subscriptionData.data.onAppAccountAdded, ...prev.apps],
        },
      });
    },
  });

  const { data: currentUserData } = useQuery(getCurrentUserInformation);

  const { data: accountsData } = useQuery(getDistributorsQuery);

  const [filteredCountries, setFilteredCountries] = useState([]);
  const [countryQuery, setCountryQuery] = useState("");

  const handleCountrySearch = (e) => {
    setCountryQuery(e.target.value);
  };

  const [updateAccount] = useMutation(updateAccountMutation);

  const [deleteAccount] = useMutation(deleteSSOAccount);

  useEffect(() => {
    if (countryQuery === "") {
      setFilteredCountries(countryData?.countries);
      return;
    }
    const filteredCountries = queryCountries(
      countryData?.countries,
      countryQuery
    );
    setFilteredCountries(filteredCountries);
  }, [countryQuery, countryData]);

  if (loading || error) {
    return <div>Loading...</div>;
  }

  const canEdit =
    accessLevel === "SUPERADMIN" || accessLevel === "DISTRIBUTOR"
      ? true
      : currentUserData?.user?.roles?.sso?.find((x) => x.accountId === id)
          ?.role !== "SUPPORTER";

  const isAccountDeletable =
    usersData?.users?.length > 0 ||
    appsData?.apps?.length > 0 ||
    !data?.account?.suspendedAt
      ? false
      : true;

  return (
    <div className="AdminAccountsEdit">
      {showDeleteModal && (
        <Modal
          acceptButtonColor="#AA2525"
          acceptText="Delete"
          content={
            <DeleteModalContent
              text={`You are about to delete ${data?.account?.name}, are you sure?`}
            />
          }
          title="Delete account"
          accept={() => {
            deleteAccount({ variables: { id: data?.account?.id } })
              .then((data) => {
                setShowDeleteModal(false);
                history.goBack();
              })
              .catch((err) => console.error(err));
          }}
          reject={() => {
            setShowDeleteModal(false);
          }}
        />
      )}
      <Formik
        initialValues={{
          name: data?.account?.name || "",
          email: data?.account?.email || "",
          vat: data?.account?.vat || "",
          address: data?.account?.address || "",
          postalCode: data?.account?.postalCode || "",
          city: data?.account?.city || "",
          country: data?.account?.country || "",
          clientId: data?.account?.clientId || "",
          parentId: data?.account?.parentId || "",
          status: data?.account?.suspendedAt ? "Suspended" : "Active",
          createdAt: formatPasswordChangeDate(data?.account.createdAt),
        }}
        onSubmit={(updatedValues) => {
          updateAccount({
            variables: {
              id: data?.account?.id,
              ...updatedValues,
            },
          })
            .then(() => {
              toast(t("toasts.accountUpdated"), toastSuccessOptions);
            })
            .catch((err) => {
              handleGraphQLErrors(err);
            });
        }}
        validationSchema={validationSchema}
      >
        {({ isValid, dirty, errors }) => {
          return (
            <Form
              style={{ display: "flex", width: "100%", overflow: "hidden" }}
            >
              <div className="AdminDistributorEdit__content">
                <EditHeader
                  type="types.account"
                  name={data?.account?.name}
                  status={
                    data?.account?.suspendedAt
                      ? "states.suspended"
                      : "states.active"
                  }
                  goBack={() => history.goBack()}
                />
                <div className="AdminDistributorEdit__companyinfo">
                  <EditableInformation
                    title={t("sectionTitles.companyInformations")}
                  >
                    <EditableInformationItemWrapper label="accounts.name">
                      <EditableInformationTextInput
                        name="name"
                        type="text"
                        canEdit={canEdit}
                      />
                    </EditableInformationItemWrapper>
                    <EditableInformationItemWrapper label="accounts.status">
                      <EditableInformationTextSelect
                        name="status"
                        options={selectOptionsForAccountStatus}
                        type="text"
                        canEdit={canEdit}
                      />
                    </EditableInformationItemWrapper>
                    <EditableInformationItemWrapper label="accounts.creationDate">
                      <EditableInformationTextInput
                        name="createdAt"
                        type="text"
                        canEdit={false}
                        disabled
                      />
                    </EditableInformationItemWrapper>
                    <EditableInformationItemWrapper label="generic.email">
                      <EditableInformationTextInput
                        name="email"
                        type="text"
                        canEdit={canEdit}
                      />
                    </EditableInformationItemWrapper>
                    <EditableInformationItemWrapper label="accounts.vat">
                      <EditableInformationTextInput
                        name="vat"
                        type="text"
                        canEdit={canEdit}
                      />
                    </EditableInformationItemWrapper>
                    <EditableInformationItemWrapper label="accounts.address">
                      <EditableInformationTextInput
                        name="address"
                        type="text"
                        canEdit={canEdit}
                      />
                    </EditableInformationItemWrapper>
                    <EditableInformationItemWrapper label="accounts.postalCode">
                      <EditableInformationTextInput
                        name="postalCode"
                        type="text"
                        canEdit={canEdit}
                      />
                    </EditableInformationItemWrapper>
                    <EditableInformationItemWrapper label="accounts.city">
                      <EditableInformationTextInput
                        name="city"
                        type="text"
                        canEdit={canEdit}
                      />
                    </EditableInformationItemWrapper>
                    <EditableInformationItemWrapper label="accounts.country">
                      <EditableInformationTextSelect
                        name="country"
                        type="text"
                        withSquare={false}
                        options={
                          filteredCountries
                            ? filteredCountries.map((country) => {
                                return {
                                  label: country.name,
                                  value: country.name,
                                };
                              })
                            : []
                        }
                        withSearch={true}
                        query={countryQuery}
                        onQueryChange={(e) => handleCountrySearch(e)}
                      />
                    </EditableInformationItemWrapper>
                    <EditableInformationItemWrapper label="accounts.clientId">
                      <EditableInformationTextInput
                        name="clientId"
                        type="text"
                        canEdit={canEdit}
                      />
                    </EditableInformationItemWrapper>
                    {canEdit && accessLevel === "SUPERADMIN" && (
                      <EditableInformationItemWrapper label="accounts.distributor">
                        <EditableInformationTextSelect
                          options={
                            accountsData?.distributors?.map((dist) => ({
                              label: dist.name,
                              value: dist.id,
                            })) || []
                          }
                          name="parentId"
                          type="text"
                          withSquare={false}
                        />
                      </EditableInformationItemWrapper>
                    )}
                  </EditableInformation>
                </div>
                <ApplicationDisplay
                  accountId={params?.id}
                  canManage={canEdit}
                  data={appsData}
                  error={appsError}
                  loading={appsLoading}
                  distributorId={data?.account?.parentId}
                  accessLevel={data?.account?.role}
                />

                <div className="AdminDistributorEdit__administrativeusers">
                  <AdminUsersControlList
                    accountId={params.id}
                    title="sectionTitles.users"
                    selectOptions={options}
                    canManage={canEdit}
                    data={usersData}
                    error={usersError}
                    loading={usersLoading}
                  />
                </div>
                {canEdit && (
                  <div className="AdminDistributorEdit__delete-account--wrapper">
                    <div className="AdminDistributorEdit__delete-account">
                      <DeleteButton
                        label="accounts.delete"
                        onClick={(e) => {
                          e.preventDefault();
                          setShowDeleteModal(true);
                        }}
                        maxWidth="148px"
                        disabled={!isAccountDeletable}
                      />
                      <p className="AdminDistributorEdit__delete-account-text">
                        {t("accounts.transferBeforeDelete")}
                      </p>
                    </div>
                  </div>
                )}
              </div>
              <div className="AdminDistributorEdit__right">
                <div className="AdminDistributorEdit__right--actions">
                  <Button
                    buttonStyle="Button__primary"
                    disabled={!isValid || !dirty}
                    type="submit"
                  >
                    {t("generic.save")}
                  </Button>
                  <Button
                    buttonStyle="Button__secondary"
                    click={(e) => {
                      e.preventDefault();
                      history.goBack();
                    }}
                  >
                    {t("generic.cancel")}
                  </Button>
                </div>
              </div>
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

const options = [
  { label: "roles.admin", value: "ADMIN" },
  { label: "roles.employee", value: "EMPLOYEE" },
];

const selectOptionsForAccountStatus = [
  {
    label: "states.suspended",
    value: "Suspended",
  },
  {
    label: "states.active",
    value: "Active",
  },
];

export const getUsers = gql`
  query users($accountIds: [String]) {
    users(accountIds: $accountIds) {
      id
      email
      name
      status
      roles
    }
  }
`;

const getAccountQuery = gql`
  query getAccount($id: String) {
    account(id: $id) {
      id
      name
      email
      address
      postalCode
      city
      vat
      country
      suspendedAt
      clientId
      role
      parentId
      createdAt
    }
  }
`;

const updateAccountMutation = gql`
  mutation updateSSOAccount(
    $id: ID!
    $name: String
    $email: String
    $status: String
    $clientId: String
    $address: String
    $postalCode: String
    $city: String
    $country: String
    $vat: String
    $parentId: String
  ) {
    updateSSOAccount(
      id: $id
      name: $name
      email: $email
      status: $status
      clientId: $clientId
      address: $address
      postalCode: $postalCode
      city: $city
      country: $country
      vat: $vat
      parentId: $parentId
    ) {
      id
      clientId
      name
      email
      vat
      address
      postalCode
      city
      country
      createdAt
      updatedAt
      newToken
      role
      parentId
      suspendedAt
      status
      information
    }
  }
`;

const deleteSSOAccount = gql`
  mutation removeSSOAccount($id: String) {
    removeSSOAccount(id: $id) {
      id
      message
    }
  }
`;

const getAppsQuery = gql`
  query apps($accountIds: [String]) {
    apps(accountIds: $accountIds) {
      id
      name
      type
      supportAccess
    }
  }
`;

const onAppAccountAddedSubscription = gql`
  subscription onAppAccountAdded($accountId: String) {
    onAppAccountAdded(accountId: $accountId) {
      id
      name
      type
      accountId
      supportAccess
    }
  }
`;

const getDistributorsQuery = gql`
  query distributors {
    distributors {
      id
      name
    }
  }
`;

const countriesQuery = gql`
  query {
    countries {
      id
      name
      code
    }
  }
`;

const validationSchema = Yup.object().shape({
  name: Yup.string().required("You must enter a name"),
  email: Yup.string().email().required("You must enter an e-mail"),
  vat: Yup.string().required("You must enter a VAT code"),
  address: Yup.string().required("You must enter an address"),
  postalCode: Yup.string().required("You must enter a postal code"),
  country: Yup.string().required("You must select a country"),
  clientId: Yup.string().required("You must enter a client id"),
  parentId: Yup.string(),
  status: Yup.string().required(),
});
